import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import './ArtisanOnboarding.css';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DEFAULT_SCHEDULE = DAYS.reduce((acc, day) => {
    acc[day] = { active: false, start: '09:00', end: '17:00' };
    return acc;
}, {});

const SKILL_OPTIONS = [
    {
        id: 'WAX_BASIC',
        name: 'Essential Body',
        description: 'Legs, Arms, Underarms',
        icon: '✨'
    },
    {
        id: 'WAX_FACE',
        name: 'Facial Artistry',
        description: 'Brows, Lip, Chin',
        icon: '💎'
    },
    {
        id: 'WAX_INTIMATE',
        name: 'Intimate Curation',
        description: 'Bikini, Brazilian, Hollywood',
        icon: '🌸'
    }
];

const TIME_OPTIONS = Array.from({ length: 17 }, (_, i) => {
    const hour = i + 6;
    return `${hour.toString().padStart(2, '0')}:00`;
});

function ArtisanOnboarding({ user, onComplete }) {
    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Step 1: Basics
    const [displayName, setDisplayName] = useState(user?.name || '');
    const [bio, setBio] = useState('');
    const [profileImage, setProfileImage] = useState(null);
    const [profileImagePreview, setProfileImagePreview] = useState(null);

    // Step 2: Skills
    const [selectedSkills, setSelectedSkills] = useState([]);

    // Step 3: Territory & Hours
    const [address, setAddress] = useState('');
    const [radius, setRadius] = useState(15);
    const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
    const [suggestions, setSuggestions] = useState([]);
    const [locationLoading, setLocationLoading] = useState(false);

    // Debounce address search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (address.length > 2 && !address.includes('Current Location')) {
                fetchSuggestions(address);
            } else {
                setSuggestions([]);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [address]);

    const fetchSuggestions = async (searchText) => {
        setLocationLoading(true);
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchText)}&countrycodes=ca&limit=5`
            );
            const data = await response.json();
            setSuggestions(data);
        } catch (err) {
            console.error("OSM Search Error", err);
        } finally {
            setLocationLoading(false);
        }
    };

    const handleSelectAddress = (place) => {
        setAddress(place.display_name);
        setSuggestions([]);
    };

    // Load existing partial profile
    useEffect(() => {
        const loadProfile = async () => {
            try {
                const res = await api.get('/api/artisan/profile');
                if (res.ok) {
                    const data = await res.json();
                    if (data.displayName) setDisplayName(data.displayName);
                    if (data.bio) setBio(data.bio);
                    if (data.profileImageUrl) setProfileImagePreview(data.profileImageUrl);
                    if (data.skills && data.skills.length > 0) setSelectedSkills([...data.skills]);
                    if (data.serviceRadiusKm) setRadius(data.serviceRadiusKm);
                    if (data.homeLocation?.address) setAddress(data.homeLocation.address);
                    if (data.weeklySchedule && Object.keys(data.weeklySchedule).length > 0) {
                        setSchedule(prev => ({ ...prev, ...data.weeklySchedule }));
                    }
                }
            } catch (e) {
                // New profile - defaults are fine
            }
        };
        loadProfile();
    }, []);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfileImage(file);
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;
                    const max_size = 800;

                    if (width > height) {
                        if (width > max_size) {
                            height *= max_size / width;
                            width = max_size;
                        }
                    } else {
                        if (height > max_size) {
                            width *= max_size / height;
                            height = max_size;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    // Compress to JPEG with 0.7 quality to keep payload well under DynamoDB 400KB limit
                    const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
                    setProfileImagePreview(compressedDataUrl);
                };
                img.src = event.target.result;
            };
            reader.readAsDataURL(file);
        }
    };

    const toggleSkill = (skillId) => {
        setSelectedSkills(prev =>
            prev.includes(skillId)
                ? prev.filter(s => s !== skillId)
                : [...prev, skillId]
        );
    };

    const updateDay = (day, field, value) => {
        setSchedule(prev => ({
            ...prev,
            [day]: { ...prev[day], [field]: value }
        }));
    };

    const canProceed = () => {
        switch (step) {
            case 1:
                const isBioValid = bio.trim().length >= 50 && bio.trim().length <= 500;
                const hasHeadshot = profileImagePreview !== null;
                return displayName.trim().length > 0 && isBioValid && hasHeadshot;
            case 2: return selectedSkills.length > 0;
            case 3: return address.trim().length > 0 && DAYS.some(d => schedule[d].active);
            default: return false;
        }
    };

    const handleSubmit = async () => {
        setError(null);
        setSaving(true);

        // Validate schedule times
        for (const day of DAYS) {
            const s = schedule[day];
            if (s.active && s.start >= s.end) {
                setError(`Invalid schedule for ${day}: end time must be after start time.`);
                setSaving(false);
                return;
            }
        }

        const payload = {
            displayName,
            bio,
            profileImageUrl: profileImagePreview || null,
            serviceRadiusKm: parseFloat(radius),
            homeLocation: {
                address,
                lat: 49.2827,
                lng: -123.1207
            },
            weeklySchedule: schedule,
            skills: selectedSkills,
            servicesOffered: []
        };

        try {
            const res = await api.put('/api/artisan/profile', payload);
            if (res.ok) {
                onComplete();
            } else {
                const data = await res.json();
                setError(data.message || 'Failed to save profile');
            }
        } catch (e) {
            setError(e.message || 'Network error. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const stepLabels = ['The Basics', 'Skills', 'Territory'];

    return (
        <div className="onboarding-overlay">
            <div className="onboarding-wizard">
                {/* Header */}
                <div className="onboarding-header">
                    <h1>Welcome to Glaminant</h1>
                    <p>Complete your profile to start receiving booking requests</p>
                </div>

                {/* Stepper */}
                <div className="onboarding-stepper">
                    {stepLabels.map((label, i) => (
                        <div
                            key={i}
                            className={`stepper-step ${step === i + 1 ? 'active' : ''} ${step > i + 1 ? 'completed' : ''}`}
                        >
                            <div className="stepper-circle">
                                {step > i + 1 ? '✓' : i + 1}
                            </div>
                            <div className="stepper-label">{label}</div>
                            {i < stepLabels.length - 1 && <div className="stepper-line" />}
                        </div>
                    ))}
                </div>

                {error && <div className="onboarding-error">{error}</div>}

                <div className="onboarding-card">
                    {/* Step 1: The Basics */}
                    {step === 1 && (
                        <>
                            <h2>Brand Identity</h2>
                            <p>Build trust with clients by sharing who you are</p>

                            <div className="onboarding-form-group">
                                <label>Display Name *</label>
                                <input
                                    type="text"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    placeholder="e.g. Jane D."
                                />
                                <small>This is how clients will see you</small>
                            </div>

                            <div className="onboarding-form-group">
                                <label>Bio ({bio.trim().length}/500) *</label>
                                <textarea
                                    value={bio}
                                    onChange={(e) => setBio(e.target.value)}
                                    placeholder="Share your experience, certifications, and what makes your service special... (Min 50 characters)"
                                    rows={4}
                                    maxLength={500}
                                />
                                {bio.trim().length > 0 && bio.trim().length < 50 && (
                                    <small style={{ color: '#d9534f' }}>Bio must be at least 50 characters.</small>
                                )}
                            </div>

                            <div className="onboarding-form-group">
                                <label>Candid / Action Photo *</label>
                                <div className="photo-upload-area">
                                    <div className="photo-preview">
                                        {profileImagePreview ? (
                                            <img src={profileImagePreview} alt="Profile" />
                                        ) : (
                                            <span className="placeholder-icon">📸</span>
                                        )}
                                    </div>
                                    <div>
                                        <label className="photo-upload-btn" htmlFor="photo-input">
                                            {profileImagePreview ? 'Change Photo' : 'Upload Photo'}
                                        </label>
                                        <input
                                            id="photo-input"
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            style={{ display: 'none' }}
                                        />
                                        <small style={{ display: 'block', marginTop: '0.5rem', color: '#6b7c74' }}>
                                            Required: A candid or action shot of you working
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Step 2: Skills */}
                    {step === 2 && (
                        <>
                            <h2>Skill Certification</h2>
                            <p>Select the service areas you are certified to perform</p>

                            <div className="skills-grid">
                                {SKILL_OPTIONS.map(skill => (
                                    <div
                                        key={skill.id}
                                        className={`skill-card ${selectedSkills.includes(skill.id) ? 'selected' : ''}`}
                                        onClick={() => toggleSkill(skill.id)}
                                    >
                                        <div className="skill-checkbox">
                                            {selectedSkills.includes(skill.id) && '✓'}
                                        </div>
                                        <div className="skill-info">
                                            <h3>{skill.icon} {skill.name}</h3>
                                            <p>{skill.description}</p>
                                            <span className="skill-tag">{skill.id}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="skill-warning">
                                <span className="warn-icon">⚠️</span>
                                <span>
                                    Please only select services you are fully certified and equipped to perform.
                                    Admin verification may be required.
                                </span>
                            </div>
                        </>
                    )}

                    {/* Step 3: Territory & Hours */}
                    {step === 3 && (
                        <>
                            <h2>Territory & Availability</h2>
                            <p>Set your service area and weekly schedule</p>

                            <div className="territory-section">
                                <div className="onboarding-form-group">
                                    <label>Home Base Address *</label>
                                    <div className="address-input-container" style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            value={address}
                                            onChange={(e) => setAddress(e.target.value)}
                                            placeholder="Street, City..."
                                            className="form-input location-search-input"
                                        />
                                        {locationLoading && <small style={{ position: 'absolute', right: '10px', top: '10px' }}>Loading...</small>}
                                        {suggestions.length > 0 && (
                                            <ul className="location-suggestions">
                                                {suggestions.map((place) => (
                                                    <li key={place.place_id} onClick={() => handleSelectAddress(place)}>
                                                        {place.display_name}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <small>Used to calculate travel time. Not shared publicly.</small>
                                </div>

                                <div className="onboarding-form-group">
                                    <label>Service Radius</label>
                                    <div className="radius-control">
                                        <div className="radius-display">
                                            <span>How far will you travel?</span>
                                            <span className="radius-value">{radius} km</span>
                                        </div>
                                        <input
                                            type="range"
                                            min="5"
                                            max="50"
                                            value={radius}
                                            onChange={(e) => setRadius(e.target.value)}
                                        />
                                        <div className="radius-labels">
                                            <span>5 km</span>
                                            <span>50 km</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="onboarding-form-group">
                                <label>Weekly Schedule *</label>
                                <small style={{ marginBottom: '0.8rem' }}>Select at least one day you're available</small>
                                <div className="schedule-mini-grid">
                                    {DAYS.map(day => (
                                        <div key={day} className={`schedule-day-row ${schedule[day].active ? 'active' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={schedule[day].active}
                                                onChange={(e) => updateDay(day, 'active', e.target.checked)}
                                            />
                                            <span className="day-name">{day}</span>
                                            <div className="time-selects">
                                                <select
                                                    disabled={!schedule[day].active}
                                                    value={schedule[day].start}
                                                    onChange={(e) => updateDay(day, 'start', e.target.value)}
                                                >
                                                    {TIME_OPTIONS.map(t => (
                                                        <option key={t} value={t}>{t}</option>
                                                    ))}
                                                </select>
                                                <span>—</span>
                                                <select
                                                    disabled={!schedule[day].active}
                                                    value={schedule[day].end}
                                                    onChange={(e) => updateDay(day, 'end', e.target.value)}
                                                >
                                                    {TIME_OPTIONS.map(t => (
                                                        <option key={t} value={t}>{t}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Navigation */}
                    <div className="onboarding-nav">
                        {step > 1 ? (
                            <button className="btn-back" onClick={() => { setError(null); setStep(s => s - 1); }}>
                                Back
                            </button>
                        ) : <div />}

                        {step < 3 ? (
                            <button
                                className="btn-next"
                                disabled={!canProceed()}
                                onClick={() => { setError(null); setStep(s => s + 1); }}
                            >
                                Continue
                            </button>
                        ) : (
                            <button
                                className="btn-next"
                                disabled={!canProceed() || saving}
                                onClick={handleSubmit}
                            >
                                {saving ? 'Saving...' : 'Complete Setup'}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Simple CSS for suggestions */}
            <style>{`
                .location-suggestions {
                    list-style: none;
                    margin: 0;
                    padding: 0;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    max-height: 200px;
                    overflow-y: auto;
                    position: absolute;
                    width: 100%;
                    background: white;
                    z-index: 1000;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                }
                .location-suggestions li {
                    padding: 10px;
                    cursor: pointer;
                    border-bottom: 1px solid #eee;
                    font-size: 0.9rem;
                    text-align: left;
                }
                .location-suggestions li:hover {
                    background-color: #f0f0f0;
                }
            `}</style>
        </div>
    );
}

export default ArtisanOnboarding;
